import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid2,
  IconButton,
  Pagination,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  alpha,
  Collapse,
  DialogTitle,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  Schedule as ClockIcon,
  CheckCircle as CheckCircleIcon,
  Replay as RotateCcwIcon,
  ExpandMore,
  ExpandLess,
  ReceiptLong as ReceiptLongIcon,
  ErrorOutline as ErrorOutlineIcon,
  ArrowForward as ArrowForwardIcon,
  Work as WorkIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useContext } from "react";
import {
  useGetAllPaidAmountListAppMutation,
  useGetAllUnPaidAmountListAppMutation,
  useGetBusinessAssociatePaymentAppMutation,
  useGetRecruiterAllReferralCommissionAppMutation,
} from "../../redux/ReferralPayment/ReferralPayments.api";
import { useGetRecruiterRefundAmountListMutation } from "../../redux/Payments/PaymentHistory.api";
import { SnackbarContext } from "../../reusable_components/components/Snackbar";
import MUIButton from "../../reusable_components/components/MUIButton";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { InfoIcon } from "lucide-react";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import UpdateIcon from '@mui/icons-material/Update';
import MUICancelButton from "../../reusable_components/components/MuiCancelButton";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LuHistory } from "react-icons/lu";
import { useFetchPaymentHistoryMutation } from "../../redux/commissionManagement/commissionManagement.api";
import { TableLoader } from "../../reusable_components/components/TableLoader";
import CloseIcon from "@mui/icons-material/Close";
import Loader from "../../reusable_components/components/Loader";
import { SimpleCommissionLoader } from "../clients/ViewClients";

const ReferralPaymentNew = () => {
  const { Profile } = useSelector?.((state) => state?.common);
  const levelTwoApproval = Profile?.profile?.levelTwoApproval;
  const userRole = Profile?.profile?.role;
  const navigate = useNavigate();
  const { Snackbar } = useContext(SnackbarContext);

  // API mutations
  const [getBusinessAssociatePaymentApp] = useGetBusinessAssociatePaymentAppMutation();
  const [getRecruiterAllReferralCommissionApp] = useGetRecruiterAllReferralCommissionAppMutation();
  const [getAllPaidAmountListApp] = useGetAllPaidAmountListAppMutation();
  const [getAllUnPaidAmountListApp] = useGetAllUnPaidAmountListAppMutation();
  const [getRecruiterRefundAmountList] = useGetRecruiterRefundAmountListMutation();
  const [getHistory] = useFetchPaymentHistoryMutation()

  // State management
  const [activeTab, setActiveTab] = useState("all");
  const [amountList, setAmountList] = useState({});
  const [currentItems, setAllAmountList] = useState([]);
  const [showTableLoader, setShowTableLoader] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [glitchAnimation, setGlitchAnimation] = useState(true);
  const [history, setHistory] = useState([])
  const [openHistory, setOpenHistory] = useState(false)
  const [showLoader, setShowLoader] = useState(false);

  // Pagination states
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [rowsPerPage] = useState(5);

  const [allPaidPayments, setAllPaidPayments] = useState([]);
  const [allUnPaidPayments, setAllUnPaidPayments] = useState([]);
  const [recoveredPayments, setRecoveredPayments] = useState([]);

  const tabs = [
    { id: "all", label: "All" },
    { id: "paid", label: "Paid" },
    { id: "unpaid", label: "Unpaid" },
    { id: "refund", label: "Refund" },
  ];

  const stats = [
    {
      label: "Total Amount",
      value: amountList?.recruiterTotalEarnings || "0",
      icon: TrendingUpIcon,
      color: "#7F56D9",
    },
    {
      label: "Paid",
      value: amountList?.recruiterPaidAmount || "0",
      icon: CheckCircleIcon,
      color: "#12B76A",
    },
    {
      label: "Unpaid",
      value: amountList?.recruiterUnPaidAmount || "0",
      icon: ClockIcon,
      color: "#F79009",
    },
    {
      label: "To Refund",
      value: amountList?.recruiterRefundAmount || "0",
      icon: RotateCcwIcon,
      color: "#F04438",
    },
  ];

  // Handler functions
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setPage(1);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const togglePaymentDetails = (index) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Glitch animation effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setGlitchAnimation(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  // API calls
  // Add a loading state
  const [isLoading, setIsLoading] = useState(true);
  const amountsDetails = async () => {
    try {
      setIsLoading(true);
      setShowLoader(true);
      const response = await getBusinessAssociatePaymentApp().unwrap();
      setAmountList(response?.response || {});
      setIsLoading(false);
      setShowLoader(false);
    } catch (error) {
      Snackbar("Network error", "error");
      setIsLoading(false);
      setShowLoader(false);
    }
  };

  const allPayments = async () => {
    try {
      const payload = { page: page ? page - 1 : 0, size: rowsPerPage || 5 };
      setShowTableLoader(true);
      const response = await getRecruiterAllReferralCommissionApp(payload).unwrap();
      setAllAmountList(response?.response?.referralCommissionDetails || []);
      setCount(response?.response?.totalItems || 0);
    } catch (error) {
      Snackbar("Network error", "error");
    } finally {
      setShowTableLoader(false);
    }
  };

  const paidPayments = async () => {
    try {
      const payload = { page: page ? page - 1 : 0, size: rowsPerPage || 5 };
      setShowTableLoader(true);
      const response = await getAllPaidAmountListApp(payload).unwrap();
      setAllPaidPayments(response?.response?.referralCommissionDetails || []);
      setCount(response?.response?.totalItems || 0);
    } catch (error) {
      Snackbar("Network error", "error");
    } finally {
      setShowTableLoader(false);
    }
  };

  const unpaidPayments = async () => {
    try {
      const payload = { page: page ? page - 1 : 0, size: rowsPerPage || 5 };
      setShowTableLoader(true);
      const response = await getAllUnPaidAmountListApp(payload).unwrap();
      setAllUnPaidPayments(response?.response?.referralCommissionDetails || []);
      setCount(response?.response?.totalItems || 0);
    } catch (error) {
      Snackbar("Network error", "error");
    } finally {
      setShowTableLoader(false);
    }
  };

  const recoverdPayments = async () => {
    try {
      const payload = {
        page: page ? page - 1 : 0,
        size: rowsPerPage || 5,
        userId: +localStorage.getItem("userId"),
      };
      setShowTableLoader(true);
      const response = await getRecruiterRefundAmountList(payload).unwrap();
      setRecoveredPayments(response?.response?.referralCommissionDetails || []);
      setCount(response?.response?.totalItems || 0);
    } catch (error) {
      Snackbar("Network error", "error");
    } finally {
      setShowTableLoader(false);
    }
  };

  useEffect(() => {
    amountsDetails();
  }, []);

  useEffect(() => {
    switch (activeTab) {
      case "all":
        allPayments();
        break;
      case "paid":
        paidPayments();
        break;
      case "unpaid":
        unpaidPayments();
        break;
      case "refund":
        recoverdPayments();
        break;
      default:
        allPayments();
    }
  }, [activeTab, page]);

  // Filtered payments based on active tab
  const getFilteredPayments = () => {
    switch (activeTab) {
      case "paid":
        return allPaidPayments;
      case "unpaid":
        return allUnPaidPayments;
      case "refund":
        return recoveredPayments;
      default:
        return currentItems;
    }
  };

  const filteredPayments = getFilteredPayments();
  // Status Badge Component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      pending: { color: "#F79009", bgColor: "#FFFAEB" },
      paid: { color: "#3ebe82ff", bgColor: "#ECFDF3" },
      approved: { color: "#12B76A", bgColor: "#ECFDF3" },
      rejected: { color: "#F04438", bgColor: "#FEF3F2" },
      refunded: { color: "#7F56D9", bgColor: "#F9F5FF" },
      rra: { color: "#ba2828ff", bgColor: "#f7f2f2ff" },
      default: { color: "#667085", bgColor: "#F2F4F7" },
    };

    const config = statusConfig[status?.toLowerCase()] || statusConfig.default;

    return (
      <Chip
        label={status === "RRA" ? "Refund & Returned" : status}
        size="small"
        sx={{
          backgroundColor: config.bgColor,
          color: config.color,
          fontWeight: 600,
          fontSize: '0.75rem',
          border: `1px solid ${config.color}30`,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: `0 4px 12px ${alpha(config.color, 0.2)}`,
          },
        }}
      />
    );
  };

  const handleGetCommissionHistory = async (item) => {
    setOpenHistory(true)
    setShowLoader(true)
    try {
      const payload = {
        referralCommissionPaymentId: item?.paymentId || item?.referralCommissionPaymentId
      }

      const res = await getHistory(payload).unwrap()
      setHistory(res?.response)
      setShowLoader(false)
    } catch (error) {
      Snackbar('Netwrok error', 'error')
      console.error("Failed to fetch history");
      setShowLoader(false)
    }
  }

  const handleCloseHistory = () => {
    setOpenHistory(false)
    setHistory([])
  }

  const PaymentItem = ({ payment, index }) => {
    const hasPaymentDetails = payment?.paymentDetails?.length > 0;
    const hasRefundDetails = payment?.refundDetails?.length > 0;
    const details = hasPaymentDetails ? payment.paymentDetails : hasRefundDetails ? payment.refundDetails : [];

    return (
      <Card
        sx={{
          mb: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          transition: "all 0.2s ease",
          fontSize: '14px',
          "&:hover": {
            borderColor: "primary.main",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          },
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            {/* Left Section */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body1" fontWeight="600" color="text.primary" sx={{ fontSize: '14px', mb: 0.5 }}>
                  {payment.referralType}-{dayjs(payment?.createdDate).format("YYYY")}-{payment?.referralCommissionId || "N/A"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                  {payment.positionName || "N/A"}
                </Typography>
              </Box>
            </Box>

            {/* Right Section */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Box sx={{ textAlign: "center", minWidth: 80 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px', mb: 0.5, }}>
                  Status
                </Typography>
                <StatusBadge status={payment?.status} />
              </Box>

              <Box sx={{ textAlign: "center", minWidth: 100 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px', mb: 0.5, }}>
                  Amount
                </Typography>
                <Typography variant="body1" fontWeight="700" color="primary.main" sx={{ fontSize: '14px' }}>
                  ₹{payment.commissionAmount || "0"}
                </Typography>
              </Box>

              {(hasPaymentDetails || hasRefundDetails) && (
                <Tooltip title={expandedItems[index] ? "Hide details" : "Show details"}>
                  <IconButton
                    size="small"
                    onClick={() => togglePaymentDetails(index)}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      width: 32,
                      height: 32,
                      transition: "all 0.3s ease",
                      transform: expandedItems[index] ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  >
                    <ExpandMore sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          <Collapse in={expandedItems[index]} timeout="auto" unmountOnExit>
            <Box sx={{
              mt: 2,
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}>
              <Typography variant="body1" fontWeight="600" color="text.primary" sx={{ fontSize: '14px', mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <ReceiptLongIcon sx={{ fontSize: 16 }} />
                {hasPaymentDetails ? 'Payment' : 'Refund'} Schedule ({details.length})
              </Typography>

              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  borderRadius: 1,
                  fontSize: '14px',
                  overflow: 'hidden',
                }}
              >
                <Table size="small" sx={{
                  border: 'none',
                  tableLayout: 'fixed',
                }}>
                  <TableHead>
                    <TableRow>
                      {hasPaymentDetails ? (
                        <>
                          <TableCell sx={{
                            fontWeight: 600,
                            fontSize: '12px',
                            py: 1.5,
                            color: 'text.secondary',
                            border: 'none',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            width: '25%' // Equal width for 4 columns
                          }}>
                            PAYMENT ID
                          </TableCell>
                          <TableCell sx={{
                            fontWeight: 600,
                            fontSize: '12px',
                            py: 1.5,
                            color: 'text.secondary',
                            border: 'none',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            width: '25%'
                          }}>
                            DUE DATE
                          </TableCell>
                          <TableCell sx={{
                            fontWeight: 600,
                            fontSize: '12px',
                            py: 1.5,
                            color: 'text.secondary',
                            border: 'none',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            width: '25%'
                          }} align="center">
                            AMOUNT
                          </TableCell>
                          <TableCell sx={{
                            fontWeight: 600,
                            fontSize: '12px',
                            py: 1.5,
                            color: 'text.secondary',
                            border: 'none',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            width: '25%'
                          }} align="center">
                            STATUS
                          </TableCell>
                          <TableCell sx={{
                            fontWeight: 600,
                            fontSize: '12px',
                            py: 1.5,
                            color: 'text.secondary',
                            border: 'none',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            width: '25%'
                          }} align="center">
                            History
                          </TableCell>
                        </>
                      ) : hasRefundDetails ? (
                        <>
                          <TableCell sx={{
                            fontWeight: 600,
                            fontSize: '12px',
                            py: 1.5,
                            color: 'text.secondary',
                            border: 'none',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            width: '25%'
                          }}>
                            REFUND ID
                          </TableCell>
                          <TableCell sx={{
                            fontWeight: 600,
                            fontSize: '12px',
                            py: 1.5,
                            color: 'text.secondary',
                            border: 'none',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            width: '25%'
                          }}>
                            POSITION
                          </TableCell>
                          <TableCell sx={{
                            fontWeight: 600,
                            fontSize: '12px',
                            py: 1.5,
                            color: 'text.secondary',
                            border: 'none',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            width: '25%'
                          }} align="center">
                            AMOUNT
                          </TableCell>
                          <TableCell sx={{
                            fontWeight: 600,
                            fontSize: '12px',
                            py: 1.5,
                            color: 'text.secondary',
                            border: 'none',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            width: '25%'
                          }} align="center">
                            STATUS
                          </TableCell>
                          <TableCell sx={{
                            fontWeight: 600,
                            fontSize: '12px',
                            py: 1.5,
                            color: 'text.secondary',
                            border: 'none',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            width: '25%'
                          }} align="center">
                            History
                          </TableCell>
                        </>
                      ) : null}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {details.map((detail, detailIndex) => (
                      <TableRow
                        key={detailIndex}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        {hasPaymentDetails ? (
                          <>
                            <TableCell sx={{
                              fontSize: '13px',
                              py: 1.5,
                              border: 'none',
                              borderBottom: '1px solid',
                              borderColor: 'grey.100',
                              fontWeight: 500,
                              width: '25%'
                            }}>
                              {detail.paymentId || "N/A"}
                            </TableCell>
                            <TableCell sx={{
                              fontSize: '13px',
                              py: 1.5,
                              border: 'none',
                              borderBottom: '1px solid',
                              borderColor: 'grey.100',
                              color: 'text.secondary',
                              width: '25%'
                            }}>
                              {detail.paymentDate ? dayjs(detail.paymentDate).format("DD MMM YYYY") : "N/A"}
                            </TableCell>
                            <TableCell sx={{
                              fontSize: '13px',
                              py: 1.5,
                              border: 'none',
                              borderBottom: '1px solid',
                              borderColor: 'grey.100',
                              width: '25%'
                            }} align="center">
                              <Typography variant="body2" fontWeight="600" color="primary.main" sx={{ fontSize: '13px' }}>
                                ₹{detail.paymentAmount || "0"}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{
                              py: 1.5,
                              border: 'none',
                              borderBottom: '1px solid',
                              borderColor: 'grey.100',
                              width: '25%'
                            }}>
                              <StatusBadge status={detail.paymentStatus} />
                            </TableCell>
                            <TableCell align="center" sx={{
                              py: 1.5,
                              border: 'none',
                              borderBottom: '1px solid',
                              borderColor: 'grey.100',
                              width: '25%'
                            }}>
                              <Box sx={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                              }}>
                                <Tooltip
                                  title="Payment History"
                                  placement="top"
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        backgroundColor: "#0A0D12",
                                        borderRadius: "8px",
                                        padding: "8px 12px",
                                        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
                                      },
                                    },
                                  }}
                                >
                                  <span>
                                    <LuHistory
                                      onClick={() => handleGetCommissionHistory(detail)}
                                      style={{
                                        fontSize: "17px",
                                        cursor: "pointer",
                                        color: "#b6bbbb",
                                        transition: "transform 0.2s ease, color 0.2s ease",
                                      }}
                                      onMouseOver={(e) => {
                                        e.currentTarget.style.color = "#5417cd";
                                        e.currentTarget.style.transform = "scale(1.2)";
                                      }}
                                      onMouseOut={(e) => {
                                        e.currentTarget.style.color = "#b6bbbb";
                                        e.currentTarget.style.transform = "scale(1)";
                                      }}
                                    />
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </>
                        ) : hasRefundDetails ? (
                          <>
                            <TableCell sx={{
                              fontSize: '13px',
                              py: 1.5,
                              border: 'none',
                              borderBottom: '1px solid',
                              borderColor: 'grey.100',
                              fontWeight: 500,
                              width: '25%'
                            }}>
                              {detail.refundId || "N/A"}
                            </TableCell>
                            <TableCell sx={{
                              fontSize: '13px',
                              py: 1.5,
                              border: 'none',
                              borderBottom: '1px solid',
                              borderColor: 'grey.100',
                              color: 'text.secondary',
                              width: '25%'
                            }}>
                              {detail.refundPositionName || "N/A"}
                            </TableCell>
                            <TableCell sx={{
                              fontSize: '13px',
                              py: 1.5,
                              border: 'none',
                              borderBottom: '1px solid',
                              borderColor: 'grey.100',
                              width: '25%'
                            }} align="center">
                              <Typography variant="body2" fontWeight="600" color="error.main" sx={{ fontSize: '13px' }}>
                                ₹{detail.refundAmount || "0"}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{
                              py: 1.5,
                              border: 'none',
                              borderBottom: '1px solid',
                              borderColor: 'grey.100',
                              width: '25%'
                            }}>
                              <StatusBadge status={detail.refundStatus} />
                            </TableCell>
                            <TableCell align="center" sx={{
                              py: 1.5,
                              border: 'none',
                              borderBottom: '1px solid',
                              borderColor: 'grey.100',
                              width: '25%'
                            }}>
                              <Box sx={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                              }}>
                                <Tooltip
                                  title="Payment History"
                                  placement="top"
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        backgroundColor: "#0A0D12",
                                        borderRadius: "8px",
                                        padding: "8px 12px",
                                        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
                                      },
                                    },
                                  }}
                                >
                                  <span>
                                    <LuHistory
                                      onClick={() => handleGetCommissionHistory(detail)}
                                      style={{
                                        fontSize: "17px",
                                        cursor: "pointer",
                                        color: "#b6bbbb",
                                        transition: "transform 0.2s ease, color 0.2s ease",
                                      }}
                                      onMouseOver={(e) => {
                                        e.currentTarget.style.color = "#5417cd";
                                        e.currentTarget.style.transform = "scale(1.2)";
                                      }}
                                      onMouseOut={(e) => {
                                        e.currentTarget.style.color = "#b6bbbb";
                                        e.currentTarget.style.transform = "scale(1)";
                                      }}
                                    />
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </>
                        ) : null}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box className="container">
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
          <SimpleCommissionLoader />
        </Box>
      ) : (
        amountList?.levelTwoApproval !== "Y" && amountList?.recruiterTotalEarnings !== 0 ? (
          <Dialog
            open={true}
            maxWidth="sm"
            sx={{
              "& .MuiDialog-paper": {
                borderRadius: 2,
                padding: 3,
                animation: glitchAnimation ? 'glitch 0.8s ease-in-out' : 'none',
              },
            }}
          >
            <DialogContent>
              <Box sx={{ textAlign: "center", py: 2 }}>
                <ErrorOutlineIcon sx={{ fontSize: 48, color: "error.main", mb: 2 }} />
                <Typography variant="h6" fontWeight="600" color="text.primary" sx={{ mb: 2 }}>
                  KYC Verification Required
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete your KYC verification to access commission details and payment information.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
              <MUIButton
                title="Complete KYC Verification"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate("/profile")}
                sx={{ px: 4 }}
              />
            </DialogActions>
          </Dialog>
        ) : (
          <Box>
            <Box sx={{
              maxWidth: "1200px",
              mx: "auto",
              px: 3,
              mb: 4
            }}>
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h4"
                  fontWeight="700"
                  color="text.primary"
                  sx={{
                    mb: 1,
                    animation: glitchAnimation ? 'glitch 0.8s ease-in-out' : 'none',
                  }}
                >
                  Total Rewards
                </Typography>
              </Box>

              {/* Stats Grid */}
              <Box sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
                gap: 3,
                mb: 4
              }}>
                {stats.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <Card
                      key={index}
                      sx={{
                        backgroundColor: "white",
                        borderRadius: "20px",
                        border: "1px solid",
                        borderColor: "divider",
                        overflow: "hidden",
                        position: "relative",
                        animation: glitchAnimation ? `glitch 0.8s ease-in-out ${0.2 + index * 0.1}s both` : 'none',
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                          borderColor: stat.color,
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: stat.color,
                          transform: 'scaleX(0)',
                          transition: 'transform 0.3s ease',
                        },
                        '&:hover::before': {
                          transform: 'scaleX(1)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Box>
                            <Typography variant="body2" color="text.secondary" fontWeight="500" sx={{ mb: 1 }}>
                              {stat.label}
                            </Typography>
                            <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>
                              ₹{stat.value}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: '50%',
                              backgroundColor: alpha(stat.color, 0.1),
                              color: stat.color,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'scale(1.1)',
                                backgroundColor: alpha(stat.color, 0.2),
                              },
                            }}
                          >
                            <Icon sx={{ fontSize: "14px" }} />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>

              {/* Tabs */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{
                  display: "inline-flex",
                  backgroundColor: "white",
                  p: 0.5,
                  borderRadius: "50px",
                  border: "1px solid",
                  borderColor: "divider",
                  animation: glitchAnimation ? 'glitch 0.8s ease-in-out 0.6s both' : 'none',
                }}>
                  {tabs.map((tab) => (
                    <Button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      sx={{
                        fontSize: '14px',
                        fontWeight: "600",
                        borderRadius: '50px',
                        textTransform: "none",
                        minWidth: "150px",
                        backgroundColor: activeTab === tab.id ? "primary.main" : "transparent",
                        color: activeTab === tab.id ? "white" : "text.secondary",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: activeTab === tab.id ? "primary.dark" : "action.hover",
                          transform: activeTab === tab.id ? 'scale(1.02)' : 'none',
                          boxShadow: activeTab === tab.id ? '0 4px 12px rgba(127, 86, 217, 0.3)' : 'none',
                        },
                      }}
                    >
                      {tab.label}
                    </Button>
                  ))}
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                {showTableLoader ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : filteredPayments?.length > 0 ? (
                  <Box sx={{ width: "100%" }}>
                    <>
                      {filteredPayments.map((payment, index) => (
                        <PaymentItem
                          key={payment.referralCommissionId || index}
                          payment={payment}
                          index={index}
                          sx={{
                            animation: glitchAnimation
                              ? `glitch 0.8s ease-in-out ${0.8 + index * 0.1}s both`
                              : "none",
                          }}
                        />
                      ))}

                      {count > rowsPerPage && (
                        <Box
                          sx={{
                            width: "100%",
                            mt: 2,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            background: "white",
                            borderRadius: 2,
                            padding: "12px 16px",
                          }}
                        >
                          <MUICancelButton
                            title="Previous"
                            startIcon={
                              <ArrowBackIcon
                                sx={{ color: page === 1 ? "#a2a2a2" : "#414651" }}
                              />
                            }
                            disabled={page === 1}
                            onClick={() => handleChangePage(null, page - 1)}
                            sx={{ height: "36px" }}
                          />

                          <Pagination
                            page={page}
                            onChange={handleChangePage}
                            count={Math.ceil(count / rowsPerPage)}
                            siblingCount={3}
                            boundaryCount={3}
                            shape="rounded"
                            hidePrevButton
                            hideNextButton
                          />

                          <MUICancelButton
                            title="Next"
                            startIcon={
                              <ArrowForwardIcon
                                sx={{
                                  color:
                                    page === Math.ceil(count / rowsPerPage)
                                      ? "#a2a2a2"
                                      : "#414651",
                                }}
                              />
                            }
                            disabled={page === Math.ceil(count / rowsPerPage)}
                            onClick={() => handleChangePage(null, page + 1)}
                            sx={{ height: "36px" }}
                          />
                        </Box>
                      )}
                    </>
                  </Box>

                ) : (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <ReceiptLongIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1, opacity: 0.5 }} />
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5, fontSize: '14px' }}>
                      No payments found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                      {`No ${activeTab !== "all" ? activeTab : ""} payments available`}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <style jsx global>{`
        @keyframes glitch {
          0% {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          50% {
            opacity: 0.5;
            transform: translateY(-5px) scale(1.02);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes slideDown {
          0% {
            opacity: 0;
            max-height: 0;
            transform: translateY(-10px);
          }
          100% {
            opacity: 1;
            max-height: 500px;
            transform: translateY(0);
          }
        }
      `}</style>

            <Grid2>
              <Dialog
                open={openHistory}
                onClose={handleCloseHistory}
                scroll="paper"
                maxWidth="md"
                sx={{
                  "& .MuiDialog-paper": {
                    minHeight: "85vh",
                    minWidth: "70vw",
                  },
                  marginLeft: "70px",
                }}
              >
                <DialogTitle variant="h6">
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>
                      Payment History
                    </Typography>
                    <CloseIcon
                      sx={{ cursor: "pointer" }}
                      onClick={handleCloseHistory}
                    ></CloseIcon>
                  </Box>
                </DialogTitle>
                <DialogContent dividers>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell className="tableHeadersCell">Updated On</TableCell>
                          <TableCell className="tableHeadersCell">Installment Due Date</TableCell>
                          <TableCell className="tableHeadersCell">Name</TableCell>
                          <TableCell className="tableHeadersCell">Invoice No</TableCell>
                          <TableCell className="tableHeadersCell">Amount</TableCell>
                          <TableCell className="tableHeadersCell">Status</TableCell>
                          <TableCell className="tableHeadersCell">Remarks</TableCell>
                        </TableRow>
                      </TableHead>

                      {!showLoader ? (
                        history?.paymentHistory?.length > 0 ? (
                          <TableBody>
                            {history?.paymentHistory?.map((item, index) => (
                              <TableRow
                                key={index}
                                sx={{
                                  backgroundColor: index % 2 === 1 ? "#FAFAFA" : "inherit",
                                  "&:hover": { backgroundColor: "#f1f1f1" },
                                }}
                              >
                                <TableCell className="tableBodyCell">
                                  {dayjs(item.generatedDate).format('DD-MM-YYYY') || '-'}
                                </TableCell>

                                <TableCell className="tableBodyCell">
                                  {dayjs(item.paymentDate).format('DD-MM-YYYY') || '-'}
                                </TableCell>

                                <TableCell className="tableBodyCell">
                                  {item.generatedUserName || '-'}
                                </TableCell>

                                <TableCell className="tableBodyCell">
                                  {item.invoiceNumber || '-'}
                                </TableCell>

                                <TableCell className="tableBodyCell">
                                  {item.paymentAmount || '-'}
                                </TableCell>

                                <TableCell className="tableBodyCell">
                                  <StatusBadge status={item?.paymentStatus} />
                                </TableCell>

                                <TableCell className="tableBodyCell" sx={{ width: '20%' }}>
                                  {item.remarks || "-"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        ) : (
                          <TableBody>
                            <TableRow>
                              <TableCell colSpan={10}>
                                <Typography className="emptydatas">No Data Found</Typography>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        )
                      ) : (
                        <TableCell colSpan={100}>
                          <Box className="tableloader">
                            <TableLoader />
                          </Box>
                        </TableCell>
                      )}
                    </Table>
                  </TableContainer>
                </DialogContent>
                <DialogActions>
                  <MUICancelButton onClick={handleCloseHistory} title="Close">
                    Close
                  </MUICancelButton>
                </DialogActions>
              </Dialog>
            </Grid2>
          </Box>
        )
      )}
    </Box>
  );
};

export default ReferralPaymentNew;